import type { Phase1JobPoll } from "./phase1Job";
import { phase1CurrentActivityLine } from "./phase1OverallPercent";

export type Phase1ProgressCopy = {
  headline: string;
  detail: string;
  phaseLabel: string;
  activityLine: string;
};

/** Human-readable explanation of what Phase 1 is doing right now. */
export function describePhase1Progress(data: {
  status?: string;
  status_detail?: string;
  progress?: Phase1JobPoll["progress"] | null;
  log?: Phase1JobPoll["log"] | null;
}): Phase1ProgressCopy {
  const status = String(data.status || "").toLowerCase();
  const p = data.progress;
  const phase = String(p?.phase || "").toLowerCase() || "starting";
  const queued = Number(p?.queries_queued ?? 0);
  const done = Number(p?.queries_done ?? p?.steps_total ?? 0);
  const urls = p?.urls_discovered;
  const scrapeDone = Number(p?.scrape_done ?? 0);
  const scrapeTotal = Number(p?.scrape_total ?? 0);
  const activityLine = phase1CurrentActivityLine(p);

  if (data.status_detail) {
    return {
      headline: "Phase 1 queued",
      detail: data.status_detail,
      phaseLabel: "queued",
      activityLine: "",
    };
  }

  if (status === "pending" || phase === "queued") {
    return {
      headline: "Phase 1 queued",
      detail:
        queued > 0
          ? `Waiting for the background worker to start (${queued} approved queries). If this stays here more than ~1 minute, use Stop job and Execute again.`
          : "Waiting for the background worker to start. If this stays here, use Stop job and Execute again.",
      phaseLabel: "queued",
      activityLine: "",
    };
  }

  if (phase === "persisting" || p?.current_activity === "saving_evidence") {
    return {
      headline: "Saving evidence",
      detail: activityLine || "Writing pages to the database…",
      phaseLabel: "persisting",
      activityLine,
    };
  }

  if (phase === "scraping" || (scrapeTotal > 0 && scrapeDone < scrapeTotal)) {
    return {
      headline: "Downloading evidence pages",
      detail:
        activityLine ||
        (scrapeTotal > 0
          ? `Firecrawl / fallback scrape: ${scrapeDone} of ${scrapeTotal} pages`
          : "Preparing to download scraped pages…"),
      phaseLabel: "scraping",
      activityLine,
    };
  }

  if (phase === "discovering" || (queued > 0 && done < queued)) {
    return {
      headline: "Running search APIs",
      detail:
        activityLine ||
        (queued > 0
          ? `Search queries: ${done} of ${queued} finished${
              urls != null ? ` · ${urls} URLs found so far` : ""
            }`
          : "Starting approved search queries…"),
      phaseLabel: "discovering",
      activityLine,
    };
  }

  if (queued > 0 && done >= queued && urls != null) {
    return {
      headline: "Search complete",
      detail:
        activityLine || `${urls} URLs discovered — preparing downloads…`,
      phaseLabel: "discovering",
      activityLine,
    };
  }

  return {
    headline: "Phase 1 starting",
    detail: activityLine || "Initializing execute pipeline…",
    phaseLabel: phase === "starting" ? "starting" : phase,
    activityLine,
  };
}
